import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';

function DashboardKPI({ user }) {
  const [myKPIs, setMyKPIs] = useState(null);
  const [colleaguesKPIs, setColleaguesKPIs] = useState([]);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, [startDate, endDate]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (user.role === 'driver' && user.driver) {
        // Mes KPIs
        const myResponse = await axios.get(`/api/kpis/driver/${user.driver.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: startDate, end_date: endDate }
        });
        setMyKPIs({
          ...myResponse.data,
          name: `${user.driver.firstname} ${user.driver.lastname}`,
          vehicle: user.driver.vehicle_plate
        });

        // Récupérer tous les chauffeurs
        const driversResponse = await axios.get('/api/drivers', {
          headers: { Authorization: `Bearer ${token}` }
        });

        // KPIs de chaque collègue
        const kpisPromises = driversResponse.data
          .filter(d => d.id !== user.driver.id)
          .map(async (driver) => {
            try {
              const kpis = await axios.get(`/api/kpis/driver/${driver.id}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { start_date: startDate, end_date: endDate }
              });
              return {
                ...kpis.data,
                id: driver.id,
                name: `${driver.firstname} ${driver.lastname}`,
                vehicle: driver.vehicle_plate
              };
            } catch (err) {
              return null;
            }
          });

        const kpis = await Promise.all(kpisPromises);
        setColleaguesKPIs(kpis.filter(k => k !== null));
      } else {
        // Admin: tous les chauffeurs
        const driversResponse = await axios.get('/api/drivers', {
          headers: { Authorization: `Bearer ${token}` }
        });

        const kpisPromises = driversResponse.data.map(async (driver) => {
          try {
            const kpis = await axios.get(`/api/kpis/driver/${driver.id}`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { start_date: startDate, end_date: endDate }
            });
            return {
              ...kpis.data,
              id: driver.id,
              name: `${driver.firstname} ${driver.lastname}`,
              vehicle: driver.vehicle_plate
            };
          } catch (err) {
            return null;
          }
        });

        const kpis = await Promise.all(kpisPromises);
        setColleaguesKPIs(kpis.filter(k => k !== null));
      }
    } catch (error) {
      console.error('Erreur dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#27ae60';
    if (score >= 80) return '#2ecc71';
    if (score >= 70) return '#f39c12';
    if (score >= 60) return '#e67e22';
    return '#e74c3c';
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return 'Excellence';
    if (score >= 80) return 'Très bon';
    if (score >= 70) return 'Bon';
    if (score >= 60) return 'Moyen';
    return 'À améliorer';
  };

  const ScoreGauge = ({ score, size = 120 }) => {
    const percentage = (score / 100) * 100;
    const color = getScoreColor(score);

    return (
      <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size/2}
            cy={size/2}
            r={(size-20)/2}
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="12"
          />
          <circle
            cx={size/2}
            cy={size/2}
            r={(size-20)/2}
            fill="none"
            stroke={color}
            strokeWidth="12"
            strokeDasharray={`${percentage * 3.14 * (size-20) / 100} ${3.14 * (size-20)}`}
            strokeLinecap="round"
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color }}>{score}</div>
          <div style={{ fontSize: '0.7rem', color: '#666' }}>/100</div>
        </div>
      </div>
    );
  };

  const KPICard = ({ kpis, name, vehicle, highlight = false }) => {
    const score = kpis.globalScore?.total || 0;
    const color = getScoreColor(score);

    return (
      <div style={{
        background: highlight
          ? 'linear-gradient(135deg, #B8472E 0%, #6E2A19 100%)'
          : 'white',
        color: highlight ? 'white' : '#2c3e50',
        padding: '2rem',
        borderRadius: '16px',
        boxShadow: highlight
          ? '0 12px 32px rgba(184, 71, 46, 0.5)'
          : '0 4px 12px rgba(0,0,0,0.1)',
        border: highlight ? '3px solid #fff' : '1px solid #e0e0e0',
        transition: 'all 0.3s',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-8px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>{name}</h3>
            {vehicle && (
              <p style={{ margin: '0.3rem 0 0 0', fontSize: '0.9rem', opacity: 0.8 }}>
                {vehicle}
              </p>
            )}
          </div>
          {highlight && (
            <span style={{
              background: 'rgba(255,255,255,0.25)',
              padding: '0.4rem 1rem',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              MOI
            </span>
          )}
        </div>

        {/* Score Global */}
        <div style={{ marginBottom: '1.5rem' }}>
          <ScoreGauge score={score} size={100} />
          <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.9rem', fontWeight: 'bold' }}>
            {getScoreBadge(score)}
          </div>
        </div>

        {/* Mini KPIs Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.8rem',
          marginBottom: '1rem'
        }}>
          <div style={{
            background: highlight ? 'rgba(255,255,255,0.15)' : '#f8f9fa',
            padding: '0.8rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8, marginBottom: '0.3rem' }}>CA</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
              {(kpis.productivity?.total_revenue || 0).toFixed(0)}€
            </div>
          </div>

          <div style={{
            background: highlight ? 'rgba(255,255,255,0.15)' : '#f8f9fa',
            padding: '0.8rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8, marginBottom: '0.3rem' }}>Jours</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
              {kpis.productivity?.working_days || 0}
            </div>
          </div>

          <div style={{
            background: highlight ? 'rgba(255,255,255,0.15)' : '#f8f9fa',
            padding: '0.8rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8, marginBottom: '0.3rem' }}>⏰ Assiduité</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
              {(kpis.attendance?.rate || 0).toFixed(0)}%
            </div>
          </div>

          <div style={{
            background: highlight ? 'rgba(255,255,255,0.15)' : '#f8f9fa',
            padding: '0.8rem',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.8, marginBottom: '0.3rem' }}>🤝 Team</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
              {(kpis.teamSpirit?.overall_score || 0).toFixed(1)}/5
            </div>
          </div>
        </div>

        {/* Détails supplémentaires */}
        <div style={{
          borderTop: highlight ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e0e0e0',
          paddingTop: '1rem',
          fontSize: '0.85rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Réclamations:</span>
            <span style={{ fontWeight: 'bold' }}>{kpis.quality?.complaints || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Retards:</span>
            <span style={{ fontWeight: 'bold' }}>{kpis.attendance?.delays || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Accidents:</span>
            <span style={{ fontWeight: 'bold' }}>{kpis.safety?.accidents || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>PV/Contraventions:</span>
            <span style={{ fontWeight: 'bold' }}>{kpis.safety?.penalties || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Montant PV:</span>
            <span style={{ fontWeight: 'bold' }}>{(kpis.safety?.penalty_amount || 0).toFixed(0)}€</span>
          </div>
        </div>

        {/* Badges */}
        {kpis.badges && kpis.badges.length > 0 && (
          <div style={{
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: highlight ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem', opacity: 0.8 }}>
              🏅 Badges:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {kpis.badges.map((badge, index) => (
                <span
                  key={index}
                  style={{
                    background: highlight ? 'rgba(255,255,255,0.2)' : '#fff3cd',
                    color: highlight ? 'white' : '#856404',
                    padding: '0.3rem 0.8rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                  title={badge.description}
                >
                  {badge.badge_type}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement des KPIs...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h2 style={{ margin: 0 }}>Tableau de bord Performance</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div>
              <label style={{ fontSize: '0.85rem', marginRight: '0.5rem' }}>Du</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', marginRight: '0.5rem' }}>Au</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
          </div>
        </div>

        {/* Ma carte (chauffeur) */}
        {user.role === 'driver' && myKPIs && (
          <div style={{ marginBottom: '3rem' }}>
            <h3 style={{
              marginBottom: '1.5rem',
              color: '#B8472E',
              fontSize: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Mes Performances
            </h3>
            <KPICard
              kpis={myKPIs}
              name={myKPIs.name}
              vehicle={myKPIs.vehicle}
              highlight={true}
            />
          </div>
        )}

        {/* Collègues */}
        {colleaguesKPIs.length > 0 && (
          <div>
            <h3 style={{
              marginBottom: '1.5rem',
              fontSize: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {user.role === 'driver' ? 'Mes Collègues' : 'Tous les Chauffeurs'}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1.5rem'
            }}>
              {colleaguesKPIs
                .sort((a, b) => (b.globalScore?.total || 0) - (a.globalScore?.total || 0))
                .map((kpis) => (
                  <KPICard
                    key={kpis.id}
                    kpis={kpis}
                    name={kpis.name}
                    vehicle={kpis.vehicle}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Aucune donnée */}
        {!myKPIs && colleaguesKPIs.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '4rem',
            color: '#999'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '1rem', fontWeight: 'bold', color: '#666' }}>Aucune donnée</div>
            <p style={{ fontSize: '1.2rem' }}>
              Aucune donnée disponible pour cette période
            </p>
            <p style={{ fontSize: '0.9rem' }}>
              Ajustez les dates ou commencez à saisir vos données quotidiennes
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardKPI;
